import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

/**
 * Hook that calls a handler when a click occurs outside the referenced element.
 *
 * Uses ref pattern for the handler to avoid re-running the effect when
 * the parent passes an inline function. The effect only re-attaches when
 * `ref` or `enabled` changes.
 */
export function useClickOutside(
	ref: RefObject<HTMLElement | null>,
	handler: () => void,
	enabled: boolean = true,
) {
	// Ref pattern: store latest handler without triggering effect re-run
	const handlerRef = useRef(handler)
	handlerRef.current = handler

	useEffect(() => {
		if (!enabled) return

		function handleClickOutside(event: MouseEvent) {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				handlerRef.current()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [ref, enabled])
}
